import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiErrorCode,
  ApiErrorCodeType,
  ApiErrorResponse,
  ValidationError,
} from '../models/api-error.model';

/**
 * Type guard — narrows an unknown caught value to {@link ApiErrorResponse}.
 * An ApiErrorResponse always has `success === false` and a `code` string.
 */
export function isApiError(err: unknown): err is ApiErrorResponse {
  return (
    typeof err === 'object' &&
    err !== null &&
    'success' in err &&
    (err as Record<string, unknown>)['success'] === false &&
    'code' in err &&
    typeof (err as Record<string, unknown>)['code'] === 'string'
  );
}

/**
 * Normalizes an Angular {@link HttpErrorResponse} into the standardized
 * {@link ApiErrorResponse} shape. Falls back gracefully when the response
 * body doesn't match the API v2.5.0 contract (e.g. proxy errors, CORS).
 */
export function extractApiError(httpErr: HttpErrorResponse): ApiErrorResponse {
  const body = httpErr.error as Record<string, unknown> | null;

  if (
    body &&
    typeof body === 'object' &&
    body['success'] === false &&
    typeof body['code'] === 'string'
  ) {
    return body as unknown as ApiErrorResponse;
  }

  // Fallback for non-contract responses.
  // Prefer body['message'] then body['error'] (some endpoints use the 'error' key)
  // before falling back to Angular's generic HTTP message.
  return {
    success: false,
    message:
      (body?.['message'] as string | undefined) ??
      (body?.['error'] as string | undefined) ??
      httpErr.message ??
      'An unexpected error occurred.',
    code: _statusToCode(httpErr.status),
    statusCode: httpErr.status,
    requestId: (body?.['requestId'] as string | undefined) ?? 'unknown',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Returns a flat `{ fieldName: errorMessage }` map from a
 * `VALIDATION_FAILED` {@link ApiErrorResponse}.
 * Returns an empty object for all other error codes.
 */
export function getFieldErrors(err: ApiErrorResponse): Record<string, string> {
  if (err.code !== ApiErrorCode.VALIDATION_FAILED) return {};
  return (err.validationErrors ?? []).reduce<Record<string, string>>(
    (acc, e: ValidationError) => ({ ...acc, [e.field]: e.message }),
    {}
  );
}

/**
 * Returns a human-readable message for the given {@link ApiErrorResponse}.
 * Optional `overrides` map allows per-code message customisation (i18n-ready).
 *
 * Falls back to `err.message` which is always populated by the API contract.
 */
export function getUserMessage(
  err: ApiErrorResponse,
  overrides?: Partial<Record<ApiErrorCodeType, string>>
): string {
  return overrides?.[err.code] ?? err.message;
}

/**
 * Returns `true` when the error is a `CONFLICT` on the specified field name.
 * Reads `err.details.field` per the API v2.5.0 contract.
 */
export function isConflictOn(err: ApiErrorResponse, field: string): boolean {
  return (
    err.code === ApiErrorCode.CONFLICT &&
    err.details?.['field'] === field
  );
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function _statusToCode(status: number): ApiErrorCodeType {
  switch (status) {
    case 400: return ApiErrorCode.VALIDATION_FAILED;
    case 401: return ApiErrorCode.UNAUTHORIZED;
    case 403: return ApiErrorCode.FORBIDDEN;
    case 404: return ApiErrorCode.NOT_FOUND;
    case 409: return ApiErrorCode.CONFLICT;
    case 422: return ApiErrorCode.BUSINESS_RULE_VIOLATION;
    case 429: return ApiErrorCode.RATE_LIMIT_EXCEEDED;
    case 500: return ApiErrorCode.INTERNAL_ERROR;
    default:  return ApiErrorCode.INTERNAL_ERROR;
  }
}
